import React from 'react';
import { Link } from 'gatsby';
import { connectHits } from 'react-instantsearch-dom';
import GlobeIcon from '../../images/globe-solid.svg';
import RowSet from '../primitives/grid/row-set';
import LocationDot from '../../../static/svgs/solid/location-dot.svg';
import PhoneIcon from '../../../static/svgs/solid/phone.svg';
import FaxIcon from '../../../static/svgs/solid/fax.svg';
import EmailIcon from '../../../static/svgs/solid/envelope.svg';
import NoResults from '../search/no-results';

import CenterIcon from '../../images/search/center.svg';
import ExternIcon from '../../images/search/extern.svg';
import HospitalIcon from '../../images/search/hospital.svg';
import MvzIcon from '../../images/search/mvz.svg';
import PressIcon from '../../images/search/press.svg';
import SingleIcon from '../../images/search/single.svg';
import TopicIcon from '../../images/search/topic.svg';
import TreatmentIcon from '../../images/search/treatment.svg';
import UserIcon from '../../images/search/user.svg';

interface SearchTileProps {
    title: string
    address?: string;
    contact?: {
        title?: string
        phone?: string
        fax?: string
        email?: string
    }[]
    link: string
    icon?: React.ReactNode
}

const SearchTile = ({ title, address, link, icon, contact }: SearchTileProps): JSX.Element => {
    const url = new URL(link);
    return (
        <Link
            css={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
            }}
            to={url.pathname}
        >
            <div css={{
                marginTop: '6px',
                width: '32px',
                height: '32px',
                marginRight: '12px',
                '@media (max-width: 767px)': {
                    width: '24px',
                    height: '24px',
                    marginRight: '6px',
                },
            }}
            >
                {icon || <GlobeIcon />}
            </div>
            <div css={{ width: '100%' }}>
                <div className="h5" dangerouslySetInnerHTML={{ __html: title }} />
                <div css={{ display: 'flex', paddingTop: '12px', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                    {address && (
                        <div css={{ display: 'flex' }}>
                            <div css={{ width: '30px', marginTop: '3px', fill: 'var(--color-red-drkrot)' }}>
                                <LocationDot css={{ width: '15px' }} />
                            </div>
                            <div css={{ width: '100%' }}>
                                <div
                                    className="p2"
                                    dangerouslySetInnerHTML={{
                                        __html: address,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {contact && contact.length > 0 && contact.map(({ phone, fax, email }, index) => (
                    <>
                        {phone && (
                            <div key={`phone_${index}`} css={{ display: 'flex', paddingTop: '12px', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                <div css={{ width: '30px', marginTop: '3px', fill: 'var(--color-red-drkrot)' }}>
                                    <PhoneIcon css={{ width: '15px' }} />
                                </div>
                                <div css={{ width: '100%' }} className="p2">
                                    {phone}
                                </div>
                            </div>
                        )}
                        {fax && (
                            <div key={`fax_${index}`} css={{ display: 'flex', paddingTop: '12px', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                <div css={{ width: '30px', marginTop: '3px', fill: 'var(--color-red-drkrot)' }}>
                                    <FaxIcon css={{ width: '15px' }} />
                                </div>
                                <div css={{ width: '100%' }} className="p2">
                                    {fax}
                                </div>
                            </div>
                        )}
                        {email && (
                            <div key={`email_${index}`} css={{ display: 'flex', paddingTop: '12px', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                <div css={{ width: '30px', marginTop: '3px', fill: 'var(--color-red-drkrot)' }}>
                                    <FaxIcon css={{ width: '15px' }} />
                                </div>
                                <div css={{ width: '100%' }} className="p2">
                                    {email}
                                </div>
                            </div>
                        )}
                    </>
                ))}
            </div>
        </Link>
    );
};

export default SearchTile;

const getTileIcon = (num?: number) => {
    switch (num) {
        case 3: // Thema
            return <TopicIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 4: // Klinik
            return <HospitalIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 5: // Behandlungzentrum
            return <CenterIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 7: // Praxis (extern)
            return <ExternIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 29: // MVZ
            return <MvzIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 15: // Treatment
            return <TreatmentIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        default:
            return <GlobeIcon />;
    }
};

export const SearchTileFacilityHits = connectHits(({ hits }: any) => (
    (
        <RowSet breakpoints={{ dt: { between: 36 } }}>
            {(hits && hits.length > 0) ? hits.map((hit: any, index: number) => {
                const icon = getTileIcon(hit.type);
                return (
                    <SearchTile
                        key={index}
                        title={hit?.post_title}
                        link={hit?.permalink}
                        icon={icon}
                        address={hit?.address}
                        contact={hit?.contact}
                    />
                );
            }) : (
                <NoResults />
            )}
        </RowSet>
    )
));
